import React, { PropsWithChildren, ReactElement } from 'react';
import Link from 'next/link';

import { TButtonProperties } from './button.types';
import { StyledLink, StyledText } from './button.styled';

/**
 * The `Button` component is used to output an `<a>` element if the "to" attribute is present or an `<button>` element if it's not.
 * Both elements look the same but assistive technologies will let user's know what to expect when clicking.
 *
 * ### Design discussions
 * - Should we have multiple sizes for buttons? This might be implemented in other apps that need to be brought in line with this design guide.
 * - Do we really need 32 colour options? Which colours do we actually want from this list?
 */
export function Button({
    children,
    to,
    target = '_self',
    color = 'purple',
    disabled = false,
    fullWidth = false,
    fullWidthOnMobileOnly = false,
    className,
    onClick,
    ...otherProperties
}: PropsWithChildren<TButtonProperties>): ReactElement | null {
    if (!children) {
        return null;
    }

    if (to) {
        if (disabled) {
            return <StyledText>{children}</StyledText>;
        }

        if (
            to.indexOf('http') === 0 ||
            to.indexOf('https') === 0 ||
            to.indexOf('tel:') === 0 ||
            to.indexOf('mailto:') === 0
        ) {
            return (
                <StyledLink
                    href={to}
                    buttonColor={color}
                    target={target}
                    disabled={disabled}
                    fullWidth={fullWidth}
                    fullWidthOnMobileOnly={fullWidthOnMobileOnly}
                    rel="noreferrer noopener"
                    className={className}
                    {...otherProperties}
                >
                    {children}
                </StyledLink>
            );
        }

        return (
            <Link href={to} passHref={true}>
                <StyledLink
                    buttonColor={color}
                    disabled={disabled}
                    fullWidth={fullWidth}
                    fullWidthOnMobileOnly={fullWidthOnMobileOnly}
                    className={className}
                    {...otherProperties}
                >
                    {children}
                </StyledLink>
            </Link>
        );
    }

    return (
        <StyledLink
            as="button"
            buttonColor={color}
            disabled={disabled}
            fullWidth={fullWidth}
            fullWidthOnMobileOnly={fullWidthOnMobileOnly}
            onClick={onClick}
            className={className}
            {...otherProperties}
        >
            {children}
        </StyledLink>
    );
}
